import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    FaCopy,
    FaIdCardAlt,
    FaInfoCircle,
    FaKey,
    FaUserLock,
    FaUsers
} from "react-icons/fa";
import { RiCustomerServiceFill } from "react-icons/ri";
import { toast } from 'react-toastify';
import {
    Alert,
    Avatar,
    CheckBox,
    InputPhone,
    Search
} from '../../components';
import { useAuth } from '../../contexts/auth';
import { useOutsideAlerter } from '../../hooks';
import {
    createAccessLevelToUser
} from '../../service/serviceChat/accessLevelService';
import {
    getGroupsUser,
    linkedGroupToUser,
    removeLinkGroupToUser
} from '../../service/serviceChat/groupService';
import {
    getUserConfig,
    maxAttendances
} from '../../service/serviceChat/userConfigService';
import {
    createUser,
    disableUser,
    generateNewPassword,
    infoReuseUser,
    reuseUser,
    updateUser
} from '../../service/serviceChat/usersService';
import { AccessLevelAction } from './AccessLevelAction';
import { GroupsAction } from './GroupsAction';
import { ImportConfigUser } from './ImportConfigUser';
import {
    TabContent,
    Actions,
    Card,
    Config,
    Container,
    Content,
    InfoUserContent,
    InfoUserCreateUpdate,
    Input,
    LoadingProgress,
    MessageAlert,
    Tab
} from './styled';


const TAB_OPTIONS = {
    ACCESS_LEVEL: "accessLevel",
    GROUPS: "groups",
}

export function UserForm({
    user,
    usersList,
    accessLevel,
    callback,
    groups
}) {
    const { functionality } = useAuth();
    const refInfoUser = useRef(null);
    const [userForm, setUserForm] = useState({
        name: '',
        username: '',
        email: '',
        phone: '',
        idd: '',
        qtdSimultaneousAttendances: 0
    });
    const [accessLevelUser, setAccessLevelUser] = useState([]);
    const [groupsUser, setGroupsUser] = useState([]);
    const [selectedFullGroups, setSelectedFullGroups] = useState([]);
    const [filterGroups, setFilterGroups] = useState('');
    const [showCredentials, setShowCredentials] = useState(null);
    const [showImportConfig, setShowImportConfig] = useState(false);
    const [importInProgress, setImportInProgress] = useState(false);
    const [disableUserMsg, setDisableUserMsg] = useState(false);
    const [confirmCreateUser, setConfirmCreateUser] = useState(null);
    const [loading, setLoading] = useState({
        inProgress: false,
        order: null
    });
    const [activeTab, setActiveTab] = useState(TAB_OPTIONS.ACCESS_LEVEL);

    const AllowConfigMaxAttendances = useMemo(() => {
        return functionality.includes('config_distribuicao_automatica')
    }, [functionality])

    const mountLinkedUser = useCallback(async (groupsByUser) => {
        if (!groups?.length) {
            setGroupsUser([]);
            return;
        }

        const groupsByUserSet = new Set(
            Array.isArray(groupsByUser)
                ? groupsByUser.map(({ id }) => id)
                : []
        );

        const updatedGroupsUsers = groups.map((group) => {
            const isLinked = groupsByUserSet.has(group.id);
            return {
                ...group,
                isLinked,
                currentLinked: isLinked,
            };
        });

        setGroupsUser(updatedGroupsUsers);
    }, [groups]);
    const searchGroupsByUser = useCallback(async (id) => {
        if (!id) return;

        try {
            const groupsByUser = await getGroupsUser(id);
            await mountLinkedUser(groupsByUser);
        } catch (error) {
            console.error('Error fetching groups for user:', error);
            await mountLinkedUser([]);
        }
    }, [mountLinkedUser]);
    useEffect(() => {
        const initializeComponent = async () => {
            setAccessLevelUser(['MEUS']);
            if (user?.id) {
                setUserForm({
                    name: '',
                    username: '',
                    email: '',
                    phone: '',
                    idd: '',
                    qtdSimultaneousAttendances: 0
                });
                await mountUser(user);
            } else {
                await mountLinkedUser([]);
            }
        };

        initializeComponent();
    }, [user, mountLinkedUser]);
    useEffect(() => {
        setSelectedFullGroups(
            groupsUser.every(obj => obj.isLinked === true)
        );
    }, [groupsUser]);

    const mountUser = async (user) => {
        setLoading({
            inProgress: true,
            order: 6
        });
        const { nomecompleto, nomeusuario, email, telefone } = user;
        const { max_atendimentos } = await getUserConfig(user?.id);
        setUserForm({
            name: nomecompleto ?? '',
            username: nomeusuario ?? '',
            email: email ?? '',
            phone: telefone ?? '',
            qtdSimultaneousAttendances: max_atendimentos ?? 0
        });
        await searchGroupsByUser(user?.id);
        if (user?.access_level?.length > 0) {
            user?.access_level?.forEach((access) => {
                onChangeAccessLevel(access?.name)
            });
        };
        setLoading({
            inProgress: false,
            order: null
        });
    };
    const onChangeAccessLevel = (value) => {
        setAccessLevelUser((prev) => {
            if (value === 'MEUS') {
                return [...prev]
            } else if (prev.includes(value)) {
                return prev.filter(access => access !== value);
            } else {
                return [...prev, value];
            }
        });
    };
    const onChangeGroup = (id, isLinked) => {
        const index = groupsUser.findIndex(group => group.id === id);
        groupsUser[index].isLinked = isLinked;
        setGroupsUser([...groupsUser]);
    };
    const onChangeNumber = (value) => {
        var newValue = 0;
        if (value.trim().length !== 0) {
            newValue = Number(value);
        }
        setUserForm((prevUserForm) => ({
            ...prevUserForm,
            qtdSimultaneousAttendances: Math.min(Math.max(parseInt(newValue), 0), 999)
        }));
    };
    const onChangeTab = (tabName) => {
        setActiveTab(tabName);
    };
    const onWaitingTime = async (time, callback) => {
        const timeoutId = setTimeout(() => {
            callback();
        }, time);
        return () => clearTimeout(timeoutId);
    };
    function onFilter(obj) {
        return obj?.nomecompleto?.toLowerCase()
            ?.includes(filterGroups?.toLowerCase())
    };
    const phoneFormatted = (idd, phone) => {
        if (!idd || !phone) {
            return phone
        }
        return `${idd.replace("+", "")}${phone.replace(/\D/g, '')}`
    };
    const haveChange = () => {
        return !(
            user?.nomecompleto === userForm?.name &&
            user?.nomeusuario === userForm?.username &&
            user?.email === userForm?.email &&
            user?.telefone === userForm?.phone
        )
    };
    const subtitleLoadingProgress = (order) => {
        let subtitle
        switch (order) {
            case 1:
                subtitle = 'Criando Usuário ...';
                break;
            case 2:
                subtitle = 'Atualizando Usuário ...';
                break;
            case 3:
                subtitle = 'Configurando Usuário ...';
                break;
            case 4:
                subtitle = 'Desabilitando Usuário ...';
                break;
            case 5:
                subtitle = 'Validando Usuário ...';
                break;
            case 6:
                subtitle = 'Buscando configurações ...';
                break;
            default:
                subtitle = '';
        }
        return subtitle
    };
    const verificationInputs = () => {
        if (!userForm?.name
            || !userForm?.username
            || !userForm?.email
            || !userForm?.phone
        ) {
            toast.warn(
                'Por favor, preencha todos os campos !',
                {
                    position: "top-center",
                    theme: "colored",
                }
            );
            setLoading({
                inProgress: false,
                order: null
            })
            return false
        }
        return true
    };
    const resetStateComponent = () => {
        const phone = userForm.phone.replace(/^55/, '')
        setUserForm((prevUserForm) => ({
            ...prevUserForm,
            phone: '',
            idd: '',
        }))
        setUserForm((prevUserForm) => ({
            ...prevUserForm,
            phone: phone,
            idd: '',
        }))
        setLoading({
            inProgress: false,
            order: null
        });
        setConfirmCreateUser(null);
    };
    async function createUserSystem(dataUser) {
        setLoading({
            inProgress: true,
            order: 1
        });
        dataUser.phone = phoneFormatted(dataUser?.idd, dataUser?.phone)
        const userCreated = await createUser(dataUser);
        if (userCreated?.status_code === 201
            || userCreated?.status_code === 403
        ) {
            return userCreated
        }
        toast.error(
            `${userCreated?.message}`,
            {
                position: "top-center",
                theme: "colored",
            }
        );
        return false
    };
    async function updateUserSystem(dataUser) {
        setLoading({
            inProgress: true,
            order: 2
        });
        if (haveChange()) {
            dataUser.phone = phoneFormatted(dataUser?.idd, dataUser?.phone)
            dataUser.id = user?.id
            const isUpdated = await updateUser(dataUser);
            if (isUpdated?.status_code === 200) {
                return user
            }
            toast.error(
                `${isUpdated?.message}`,
                {
                    position: "top-center",
                    theme: "colored",
                }
            );
            return false
        }
        return user
    };
    async function handGenerateNewPassword() {
        setLoading({
            inProgress: true,
            order: 4
        });
        const password = await generateNewPassword({
            id: user?.id,
            username: user?.nomeusuario
        });
        if (password?.status_code === 200) {
            toast.success(
                `Senha gerada com sucesso`,
                {
                    position: "top-center",
                    theme: "colored",
                }
            );
            setShowCredentials(password)
        } else {
            toast.error(
                `${password?.message}`,
                {
                    position: "top-center",
                    theme: "colored",
                }
            );
        }
        setLoading({
            inProgress: false,
            order: 4
        });
    };
    async function handImportConfig(userSelected) {
        setImportInProgress(true)
        setAccessLevelUser(['MEUS']);
        await searchGroupsByUser(userSelected?.id);
        if (userSelected?.access_level?.length > 0) {
            userSelected?.access_level?.forEach((access) => {
                onChangeAccessLevel(access?.name)
            });
        };
        setImportInProgress(false)
        setShowImportConfig(false)
    };
    async function linkedUserInCompany() {
        const dataUser = {
            id: confirmCreateUser.id,
            accessLevel: accessLevelUser,
            groups: groupsUser.filter(group => group.isLinked === true)
                .map(group => group.id),
        };
        setConfirmCreateUser(null);
        const { status_code, message } = await reuseUser(dataUser);
        const typeAlert = (status_code === 200) ? 'success' : 'error'
        toast[typeAlert](
            `${message}`,
            {
                position: "top-center",
                theme: "colored",
            }
        );
        resetStateComponent();
        if (typeAlert === 'success') callback();
    };
    async function createAccessLevelAndGroupsForUser(userID) {
        const accessLevelLinked = await createAccessLevelToUser(
            userID,
            accessLevelUser
        )
        if (!accessLevelLinked) {
            return {
                success: false,
                message: 'Erro ao vincular nível de acesso ao usuário !'
            }
        }

        // add full groups isLinked === true
        const linkedGroups = await linkedGroupToUser(
            userID,
            groupsUser.filter(group => group.isLinked === true)
                .map(group => group.id)
        )
        if (!linkedGroups) {
            return {
                success: false,
                message: 'Erro ao vincular de usuário com o setor !'
            }
        }

        // remove full groups currentLinked === true && isLinked === false
        const removeLinkGroup = await removeLinkGroupToUser(
            userID,
            groupsUser.filter(group =>
                group.isLinked === false && group.currentLinked === true
            ).map(group => group.id)
        )
        if (!removeLinkGroup) {
            return {
                success: false,
                message: 'Erro ao remover vinculo de usuário com o setor !'
            }
        }

        return {
            success: true,
            message: 'Usuário configurado com sucesso !'
        }
    };
    async function handSave() {
        setLoading({
            inProgress: true,
            order: 1
        });
        if (verificationInputs()) {
            const isCreation = !(user)
            const __user = (isCreation)
                ? await createUserSystem(userForm)
                : await updateUserSystem(userForm);

            if (__user.id) {
                setLoading({
                    inProgress: true,
                    order: 3
                });
                if (__user?.duplicateUser) {
                    const {
                        status_code,
                        message,
                        user
                    } = await infoReuseUser(__user?.id)
                    if (status_code !== 200) {
                        toast['error'](
                            `${message}`,
                            {
                                position: "top-center",
                                theme: "colored",
                            }
                        );
                        resetStateComponent();
                    } else {
                        setLoading({
                            inProgress: true,
                            order: 5
                        });
                        setConfirmCreateUser(user);
                    };
                } else {
                    const { success, message } = await createAccessLevelAndGroupsForUser(
                        __user.id
                    )
                    if (success && AllowConfigMaxAttendances) {
                        await maxAttendances(
                            __user.id,
                            userForm.qtdSimultaneousAttendances
                        )
                    }
                    const typeAlert = (success) ? 'success' : 'error'
                    toast[typeAlert](
                        `${message}`,
                        {
                            position: "top-center",
                            theme: "colored",
                        }
                    );
                    if (typeAlert === 'success') {
                        if (isCreation) {
                            setShowCredentials(__user)
                        } else {
                            callback()
                        }
                    } else {
                        resetStateComponent();
                    }
                }
            }
        } else {
            resetStateComponent();
        }
    };
    async function handDisableUser() {
        setDisableUserMsg(false);
        setLoading({
            inProgress: true,
            order: 4
        });
        const isDisabled = await disableUser(user?.id);
        onWaitingTime(3000, () => {
            if (isDisabled?.status_code === 200) {
                toast.success(
                    `Desativado com sucesso`,
                    {
                        position: "top-center",
                        theme: "colored",
                    }
                );
            } else {
                toast.error(
                    `${isDisabled?.message}`,
                    {
                        position: "top-center",
                        theme: "colored",
                    }
                );
            };
            callback();
            setLoading({
                inProgress: false,
                order: null
            });
        })
    };
    const UserInfo = ({ isOpen }) => {
        const refUsername = useRef(null);
        const refPassword = useRef(null);

        async function handCopy(ref) {
            ref.current.select();
            document.execCommand('copy');
            toast.success('copiado para area de Transferência',
                {
                    position: "top-center",
                    autoClose: 5000,
                    icon: <FaCopy />
                });
        };

        return (
            <InfoUserCreateUpdate isOpen={isOpen}>
                <div>
                    <span className='title'>
                        <div>
                            {`Credenciais`}
                        </div>
                    </span>
                    <div>
                        <Avatar
                            name={userForm.name}
                            width={'100px'}
                            height={'100px'}
                            borderRadius={'4px'}
                        />
                        <div className='trw'>
                            <span>
                                <FaIdCardAlt
                                    fontSize={'1rem'}
                                />
                            </span>
                            <input
                                ref={refUsername}
                                className='pd3'
                                type="text"
                                value={showCredentials?.username}
                                disabled={false}
                            />
                            <div className='trs'
                                onClick={
                                    () => { handCopy(refUsername) }
                                }
                            >
                                <FaCopy />
                            </div>
                        </div>
                        <div className='trw'>
                            <span>
                                <FaKey
                                    fontSize={'1rem'}
                                />
                            </span>
                            <input
                                ref={refPassword}
                                type="text"
                                value={showCredentials?.password}
                                disabled={false}
                            />
                            <div className='trs'
                                onClick={
                                    () => { handCopy(refPassword) }
                                }
                            >
                                <FaCopy />
                            </div>
                        </div>
                    </div>
                    <button onClick={() => { callback() }}>
                        {`Ok`}
                    </button>
                </div>
            </InfoUserCreateUpdate>
        )
    };
    const ConfirmCreateUser = ({ user }) => {
        return (
            <div className='cfCrUs'>
                <div>
                    <p>
                        {`Login: ${user?.nomeusuario}`}
                    </p>
                    <p>
                        {`Nome: ${user?.nomecompleto}`}
                    </p>
                    <p>
                        {`Este nome de usuário (login) já está em uso.`}
                    </p>
                    <p>
                        {`Confirma a liberação nesta empresa ? `}
                    </p>
                </div>
            </div>
        )
    };
    const InfoUser = () => {
        const [open, setOpen] = useState(false);
        useOutsideAlerter(refInfoUser, () => setOpen(false));
        return (
            <InfoUserContent ref={refInfoUser}>
                <FaInfoCircle fontSize={15} onClick={() => setOpen(!open)} />
                <div className={`${(open ? 'vis--' : '')}`}>
                    <ul>
                        <li>
                            <span>
                                {`Código:`}
                            </span>
                            <span>
                                {user?.id}
                            </span>
                        </li>
                    </ul>
                </div>
            </InfoUserContent>
        )
    };

    return (
        <>
            <Container>
                <Content>
                    <Card>
                        <div className='title'>
                            <div>
                                <FaIdCardAlt fontSize={'1.3rem'} />
                                <span>
                                    {`Dados`}
                                </span>
                            </div>
                            {(user?.id) &&
                                <div>
                                    <InfoUser />
                                </div>
                            }
                        </div>
                        <div>
                            <Input
                                id="name"
                                name="name"
                                type="text"
                                className='x-60'
                                placeholder='Nome Completo'
                                value={userForm?.name}
                                onChange={(e) => setUserForm((prevUserForm) => ({
                                    ...prevUserForm,
                                    name: e.target.value
                                }))}
                            />
                            <Input
                                id="username"
                                name="username"
                                type="text"
                                className='x-40'
                                placeholder='Nome de Usuário'
                                value={userForm?.username}
                                disabled={(user) ? true : false}
                                onChange={(e) => setUserForm((prevUserForm) => ({
                                    ...prevUserForm,
                                    username: e.target.value.replaceAll(" ", "")
                                }))}
                            />
                        </div>
                        <div>
                            <Input
                                id="email"
                                name="email"
                                type="email"
                                className='x-60'
                                placeholder='Email'
                                value={userForm?.email}
                                onChange={(e) => setUserForm((prevUserForm) => ({
                                    ...prevUserForm,
                                    email: e.target.value
                                }))}
                            />
                            <InputPhone
                                id='phoneContact'
                                name={'phoneContact'}
                                className='x-40 phone'
                                value={userForm?.phone}
                                onChange={(idd, phone) => setUserForm((prevUserForm) => ({
                                    ...prevUserForm,
                                    phone: phone,
                                    idd: idd,
                                }))}
                            />
                        </div>
                    </Card>
                    {(AllowConfigMaxAttendances) &&
                        (<Card>
                            <div className='title'>
                                <div>
                                    <RiCustomerServiceFill />
                                    {`Quantidade de atendimentos simultâneos`}
                                </div>
                                <div>
                                    <Input
                                        id="simultaneousAttendances"
                                        name="simultaneousAttendances"
                                        type="number"
                                        className='qsAtt'
                                        min={0}
                                        max={999}
                                        value={userForm?.qtdSimultaneousAttendances}
                                        onChange={(e) => onChangeNumber(e.target.value)}
                                    />
                                </div>
                            </div>
                        </Card>)
                    }
                    <Config>
                        <Card>
                            <Card>
                                <Tab>
                                    <div
                                        className={`${activeTab === TAB_OPTIONS.ACCESS_LEVEL ? 'select' : ''}`}
                                        onClick={() => onChangeTab('accessLevel')}>
                                        <div>
                                            <FaUserLock fontSize={'1.2rem'} />
                                            <span>
                                                {`Permissões`}
                                            </span>
                                        </div>
                                    </div>
                                    <div
                                        className={`${activeTab === TAB_OPTIONS.GROUPS ? 'select' : ''}`}
                                        onClick={() => onChangeTab('groups')}
                                    >
                                        <div>
                                            <FaUsers fontSize={'1.2rem'} />
                                            <span>
                                                {`Setores`}
                                            </span>
                                        </div>
                                    </div>
                                </Tab>
                            </Card>
                            <Card className='wrapConf scroll-custom'>
                                <div>
                                    <TabContent className={`${(activeTab !== TAB_OPTIONS.ACCESS_LEVEL) ? 'ul-h' : ''}`}>
                                        <ul>
                                            <div>
                                                {accessLevel?.map((access) => (
                                                    <AccessLevelAction
                                                        key={access?.id}
                                                        access={access}
                                                        initialValue={accessLevelUser?.includes(access?.name)}
                                                        onChange={(e) => onChangeAccessLevel(e)}
                                                    />
                                                ))}
                                            </div>
                                        </ul>
                                    </TabContent>
                                    <TabContent className={`${(activeTab !== TAB_OPTIONS.GROUPS) ? 'ul-h' : ''}`}>
                                        <div className='controls--groups'>
                                            <div>
                                                <Search
                                                    value={filterGroups}
                                                    onChange={(e) => setFilterGroups(e.target.value)}
                                                />
                                            </div>
                                            <div>
                                                {`Selecionar todos`}
                                                <div>
                                                    <CheckBox
                                                        initialValue={selectedFullGroups}
                                                        onChange={(isChecked) => {
                                                            var aux = []
                                                            groupsUser.forEach((group) => {
                                                                group.isLinked = isChecked
                                                                aux.push(group)
                                                            })
                                                            setGroupsUser(aux)
                                                            setSelectedFullGroups(isChecked)
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <ul>
                                            <div>
                                                {(groupsUser?.filter(onFilter)?.map((group) => {
                                                    return (
                                                        <GroupsAction
                                                            key={group?.id}
                                                            group={group}
                                                            initialValue={group?.isLinked}
                                                            onChange={(isLinked) => {
                                                                onChangeGroup(
                                                                    group.id,
                                                                    isLinked
                                                                )
                                                            }}
                                                        />
                                                    )
                                                }))}
                                            </div>
                                        </ul>
                                    </TabContent>
                                </div>
                            </Card>
                        </Card>
                    </Config>
                    <Card>
                        <Actions className='jcSB'>
                            {(user && (user?.ativo === "S"))
                                ? (
                                    <div>
                                        <span
                                            className='dsUs'
                                            onClick={() => setDisableUserMsg(true)}
                                        >
                                            {`Desativar`}
                                        </span>
                                        <span onClick={() => handGenerateNewPassword()}>
                                            {`Gerar nova Senha`}
                                        </span>
                                    </div>
                                )
                                :
                                (<span
                                    className='imCUs'
                                    onClick={() => setShowImportConfig(true)}
                                >
                                    {`Importar configurações`}
                                </span>)
                            }
                            <button
                                onClick={() => handSave()}
                            >
                                {`Salvar`}
                            </button>
                        </Actions>
                    </Card>
                </Content>
            </Container>
            <LoadingProgress
                isOpen={
                    (loading?.inProgress) ? 1 : 0
                }
            >
                <div>
                    <div>
                        <span className='loader'></span>
                    </div>
                    <div>
                        <p>
                            {subtitleLoadingProgress(loading?.order)}
                        </p>
                    </div>
                </div>
            </LoadingProgress>
            <UserInfo isOpen={showCredentials} />
            <ImportConfigUser
                isOpen={(showImportConfig ? 1 : 0)}
                users={usersList}
                inProgress={(importInProgress ? 1 : 0)}
                onClose={() => setShowImportConfig(false)}
                onClick={(e) => { handImportConfig(e) }}
            />
            {(confirmCreateUser) &&
                (<Alert
                    isOpen={true}
                    title={`Usuário já existente !`}
                    onClickOption1={
                        () => { linkedUserInCompany() }
                    }
                    onClickOption2={
                        () => { resetStateComponent() }
                    }
                >
                    <ConfirmCreateUser
                        user={confirmCreateUser}
                    />
                </Alert>)
            }
            {(disableUserMsg) &&
                (<Alert
                    isOpen={true}
                    onClickOption1={
                        () => handDisableUser()
                    }
                    onClickOption2={
                        () => setDisableUserMsg(false)
                    }
                >
                    <MessageAlert>
                        <p>
                            {`Deseja realmente desativar o usuário ?`}
                        </p>
                    </MessageAlert>
                </Alert>)
            }
        </>
    );
};
